import * as THREE from 'three';

import { getRandom } from './utils';
import SpriteText from 'three-spritetext';

/**
 * @param {[object]} texture [texture loaded from SceneController] 
 * @param {[vec3]} pos [position of the plane]
 */
export default class ImagePlane {
  constructor(scene, topTexture, bgrTexture, pos) {
    this.scene = scene;
    this.topTexture = topTexture;
    this.bgrTexture = bgrTexture;
    this.pos = pos;
    this.isAnimating = true;
  }

  setup() {
    this.mouse = new THREE.Vector2(0, 0);

    this.uniforms = {
      uTopImage: { value: this.topTexture },
      uBgrImage: { value: this.bgrTexture },
      uTime: { value: 0 },
      uRes: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      uColourMult: { value: new THREE.Vector4(0.65, 0.53, 0.1, 1.) }
      //uColourMult: { value: new THREE.Vector4(0.74, 0.89, 0.75, 1.) }
      // uColourMult: { value: new THREE.Vector4(0.175, 0.213, 0.191, 1.) }
    };

    const height = 40;
    const width = 40;
    const segments = 1; //128;
    let geometry = new THREE.PlaneBufferGeometry(width, height, segments, segments);
    let material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: `
          varying vec2 vUv;
          void main() 
          {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * 
                  vec4(position, 1.0);
          }`,
      fragmentShader: `
          precision mediump float;

          uniform vec2 uRes;
          uniform vec2 uMouse;
          uniform float uTime;
          uniform sampler2D uBgrImage;
          uniform sampler2D uTopImage;
          uniform vec4 uColourMult;
  
          varying vec2 vUv;

          float circle(vec2 st, float radius, float blurriness)
          {
            vec2 dist = st;
            return 1.-smoothstep(radius-(radius*blurriness), radius+(radius*blurriness), dot(dist,dist)*4.0);
          }

          float rect(vec2 st, vec2 size)
          {
            size = vec2(0.5) - size*0.5;
            vec2 uv = smoothstep(size, size+vec2(0.001), st);
            uv *= smoothstep(size, size+vec2(0.001), vec2(1.0)-st);
            return uv.x*uv.y;
          }

          vec3 mod289(vec3 x) 
          {
           return x - floor(x * (1.0 / 289.0)) * 289.0;
          }

          vec4 mod289(vec4 x) 
          {
           return x - floor(x * (1.0 / 289.0)) * 289.0;
          }

          vec4 permute(vec4 x) 
          {
            return mod289(((x*34.0)+1.0)*x);
          }

          vec4 taylorInvSqrt(vec4 r)
          {
            return 1.79284291400159 - 0.85373472095314 * r;
          }

          float snoise3(vec3 v)
          {
            const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
            const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

            // First corner
            vec3 i  = floor(v + dot(v, C.yyy) );
            vec3 x0 =   v - i + dot(i, C.xxx) ;

            // Other corners
            vec3 g = step(x0.yzx, x0.xyz);
            vec3 l = 1.0 - g;
            vec3 i1 = min( g.xyz, l.zxy );
            vec3 i2 = max( g.xyz, l.zxy );

            //   x0 = x0 - 0.0 + 0.0 * C.xxx;
            //   x1 = x0 - i1  + 1.0 * C.xxx;
            //   x2 = x0 - i2  + 2.0 * C.xxx;
            //   x3 = x0 - 1.0 + 3.0 * C.xxx;
            vec3 x1 = x0 - i1 + C.xxx;
            vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y
            vec3 x3 = x0 - D.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y

            // Permutations
            i = mod289(i);
            vec4 p = permute( permute( permute(
                        i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                    + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
                    + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

            // Gradients: 7x7 points over a square, mapped onto an octahedron.
            // The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)
            float n_ = 0.142857142857; // 1.0/7.0
            vec3  ns = n_ * D.wyz - D.xzx;

            vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)

            vec4 x_ = floor(j * ns.z);
            vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)
    
            vec4 x = x_ *ns.x + ns.yyyy;
            vec4 y = y_ *ns.x + ns.yyyy;
            vec4 h = 1.0 - abs(x) - abs(y);

            vec4 b0 = vec4( x.xy, y.xy );
            vec4 b1 = vec4( x.zw, y.zw );

            //vec4 s0 = vec4(lessThan(b0,0.0))*2.0 - 1.0;
            //vec4 s1 = vec4(lessThan(b1,0.0))*2.0 - 1.0;
            vec4 s0 = floor(b0)*2.0 + 1.0;
            vec4 s1 = floor(b1)*2.0 + 1.0;
            vec4 sh = -step(h, vec4(0.0));

            vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
            vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

            vec3 p0 = vec3(a0.xy,h.x);
            vec3 p1 = vec3(a0.zw,h.y);
            vec3 p2 = vec3(a1.xy,h.z);
            vec3 p3 = vec3(a1.zw,h.w);

            //Normalise gradients
            vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
            p0 *= norm.x;
            p1 *= norm.y;
            p2 *= norm.z;
            p3 *= norm.w;

            // Mix final noise value
            vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
            m = m * m;
            return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1),
                                            dot(p2,x2), dot(p3,x3) ) );
          }
  
          void main() 
          {
            vec2 res = uRes * PR;
            vec2 st = gl_FragCoord.xy / res.xy - vec2(0.5);
            st.y *= res.y / res.x;

            vec2 mouse = uMouse * -0.5;
            vec2 circlePos = st + mouse;
            float c = circle(circlePos, 0.15, 2.) * 2.5;
            //float c = circle(circlePos, 0.025, 1.) * 2.;

            float offx = vUv.x + sin(vUv.y + uTime * 0.1);
            float offy = vUv.y - uTime * 0.1 - cos(uTime * .001) * .01;

            float n = snoise3(vec3(offx, offy, uTime * .1) * 8.) - 1.;
            //float finalMask = smoothstep(0.4, 0.5, n + pow(c, 2.));
            float finalMask = smoothstep(0.4, 0.5, pow(c, 2.));

            vec4 colourShift = vec4(cos(uTime), uColourMult.g, uColourMult.b, uColourMult.a); 
            vec4 topImage = texture2D(uTopImage, vUv) * uColourMult; //colourShift;
            vec4 bgrImage = texture2D(uBgrImage, vUv);
            vec4 finalImage = mix(topImage, bgrImage, finalMask);
            gl_FragColor = finalImage;
          }`,
      //   side: THREE.DoubleSide,
      defines: {
        PR: window.devicePixelRatio.toFixed(1)
      }
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = "imagePlane";
    this.mesh.position.set(this.pos.x, this.pos.y, this.pos.z);
    let angle = getRandom(-1., 1.);
    this.mesh.rotateY(angle);
    this.scene.add(this.mesh);
  };

  createText(textArr) {
    let text = new SpriteText(textArr, 2);
    this.mesh.add(text);
    text.position.set(1, 1, 5);
    text.fontFace = 'Sulphur Point';
  }

  update() {
    this.uniforms.uTime.value += 0.01;
    if (this.isAnimating) this.mesh.rotation.y += 0.007;
  };

  onMouseMove(e) {
    this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = - (e.clientY / window.innerHeight) * 2 + 1;
  };

  onMouseDown(e) {
    this.isAnimating = false;
  };

  onMouseUp(e) {
    this.isAnimating = true;
  };
}  
